import { useState } from "react";
import { motion } from "framer-motion";
import {
  User,
  ShoppingBag,
  Bot,
  Store,
  Camera,
  Plus,
  Search,
  ExternalLink,
  CheckCircle2,
  XCircle,
  AlertCircle,
} from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn, formatPrice, formatNumber } from "@/lib/utils";
import type { Product, MarketplaceConnection, AgentConfig } from "@/types";

// Mock data
const mockProducts: Product[] = [
  { id: "p1", tenant_id: "t1", name: "Fone Bluetooth Pro", description: "Fone sem fio com ANC", price: 19900, discount_price: 12900, image_url: null, sku: "FBP-001", stock: 45, active: true },
  { id: "p2", tenant_id: "t1", name: "Carregador Turbo 65W", description: "USB-C rapido", price: 8900, discount_price: 5900, image_url: null, sku: "CT65-002", stock: 120, active: true },
  { id: "p3", tenant_id: "t1", name: "Capa iPhone 15", description: "Silicone premium", price: 4900, discount_price: null, image_url: null, sku: "CI15-003", stock: 300, active: true },
  { id: "p4", tenant_id: "t1", name: "Hub USB-C 7-em-1", description: "HDMI 4K + USB 3.0", price: 15900, discount_price: 11900, image_url: null, sku: "HUB-004", stock: 0, active: false },
];

const mockAgentConfigs: (AgentConfig & { channelName: string })[] = [
  { id: "a1", channel_id: "c1", enabled: true, personality: "Vendedor amigavel", response_delay_ms: 2000, auto_reply: true, knowledge_base: [], channelName: "Ofertas SP Capital" },
  { id: "a2", channel_id: "c2", enabled: false, personality: "Atendente tecnico", response_delay_ms: 1500, auto_reply: false, knowledge_base: [], channelName: "Promos RJ" },
  { id: "a3", channel_id: "c3", enabled: true, personality: "Consultor de vendas", response_delay_ms: 3000, auto_reply: true, knowledge_base: [], channelName: "Vendas Diretas" },
];

const mockMarketplaces: MarketplaceConnection[] = [
  { id: "m1", tenant_id: "t1", platform: "shopee", status: "connected", connected_at: "2024-11-15", products_synced: 42 },
  { id: "m2", tenant_id: "t1", platform: "mercadolivre", status: "connected", connected_at: "2024-10-20", products_synced: 38 },
  { id: "m3", tenant_id: "t1", platform: "amazon", status: "disconnected", connected_at: null, products_synced: 0 },
  { id: "m4", tenant_id: "t1", platform: "magalu", status: "expired", connected_at: "2024-08-01", products_synced: 15 },
];

const marketplaceMeta: Record<string, { color: string; logo: string }> = {
  shopee: { color: "text-orange-400", logo: "S" },
  mercadolivre: { color: "text-yellow-400", logo: "ML" },
  amazon: { color: "text-amber-400", logo: "A" },
  magalu: { color: "text-blue-400", logo: "M" },
};

function ContaTab() {
  return (
    <div className="space-y-6">
      {/* Profile Card */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row items-center gap-6">
            <div className="relative">
              <Avatar className="h-20 w-20">
                <AvatarImage src={undefined} />
                <AvatarFallback className="text-xl">JS</AvatarFallback>
              </Avatar>
              <button
                className="absolute -bottom-1 -right-1 flex h-7 w-7 items-center justify-center rounded-full bg-primary text-white shadow-lg"
                aria-label="Alterar avatar"
              >
                <Camera className="h-3.5 w-3.5" />
              </button>
            </div>
            <div className="text-center sm:text-left">
              <h3 className="text-lg font-semibold text-foreground">
                Joao Silva
              </h3>
              <p className="text-sm text-muted-foreground">
                joao@dispara.com
              </p>
              <Badge variant="default" className="mt-2">
                Plano Pro
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Usage Meters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Uso do Plano</CardTitle>
          <CardDescription>Consumo mensal do seu plano Pro</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div>
            <div className="mb-2 flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Dispatches</span>
              <span className="font-medium text-foreground">
                {formatNumber(1240)} / {formatNumber(5000)}
              </span>
            </div>
            <Progress value={24.8} indicatorClassName="bg-gradient-to-r from-primary to-indigo-400" />
          </div>
          <div>
            <div className="mb-2 flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Mensagens</span>
              <span className="font-medium text-foreground">
                {formatNumber(15420)} / {formatNumber(50000)}
              </span>
            </div>
            <Progress value={30.8} indicatorClassName="bg-gradient-to-r from-emerald-500 to-emerald-400" />
          </div>
          <div>
            <div className="mb-2 flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Grupos</span>
              <span className="font-medium text-foreground">
                48 / 100
              </span>
            </div>
            <Progress value={48} indicatorClassName="bg-gradient-to-r from-sky-500 to-sky-400" />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function CatalogoTab() {
  const [search, setSearch] = useState("");
  const filtered = mockProducts.filter(
    (p) =>
      !search || p.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Buscar produtos..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Button size="sm" className="gap-1.5">
          <Plus className="h-4 w-4" />
          Adicionar
        </Button>
      </div>

      <div className="space-y-2">
        {filtered.map((product, i) => (
          <motion.div
            key={product.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05 }}
          >
            <Card>
              <CardContent className="flex items-center gap-4 p-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-secondary">
                  <ShoppingBag className="h-5 w-5 text-muted-foreground" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-foreground truncate">
                      {product.name}
                    </p>
                    {!product.active && (
                      <Badge variant="secondary" className="text-[10px]">
                        Inativo
                      </Badge>
                    )}
                    {product.stock === 0 && (
                      <Badge variant="destructive" className="text-[10px]">
                        Sem estoque
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    SKU: {product.sku} | Estoque: {product.stock}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  {product.discount_price ? (
                    <>
                      <p className="text-sm font-semibold text-success">
                        {formatPrice(product.discount_price)}
                      </p>
                      <p className="text-xs text-muted-foreground line-through">
                        {formatPrice(product.price)}
                      </p>
                    </>
                  ) : (
                    <p className="text-sm font-semibold text-foreground">
                      {formatPrice(product.price)}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

function AgenteTab() {
  const [configs, setConfigs] = useState(mockAgentConfigs);

  const toggleAgent = (id: string) => {
    setConfigs((prev) =>
      prev.map((c) => (c.id === id ? { ...c, enabled: !c.enabled } : c))
    );
  };

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        Configure o agente AI para cada canal. O agente responde automaticamente
        com base na sua base de conhecimento.
      </p>
      <Separator />
      {configs.map((config, i) => (
        <motion.div
          key={config.id}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: i * 0.05 }}
        >
          <Card>
            <CardContent className="flex items-center justify-between p-4">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <Bot
                    className={cn(
                      "h-4 w-4",
                      config.enabled ? "text-primary" : "text-muted-foreground"
                    )}
                  />
                  <p className="text-sm font-medium text-foreground">
                    {config.channelName}
                  </p>
                </div>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {config.personality} | Delay: {config.response_delay_ms}ms
                </p>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                {config.auto_reply && (
                  <Badge variant="secondary" className="text-[10px]">
                    Auto-reply
                  </Badge>
                )}
                <Switch
                  checked={config.enabled}
                  onCheckedChange={() => toggleAgent(config.id)}
                  aria-label={`Toggle agente para ${config.channelName}`}
                />
              </div>
            </CardContent>
          </Card>
        </motion.div>
      ))}
    </div>
  );
}

function MarketplacesTab() {
  const statusIcon = (status: string) => {
    switch (status) {
      case "connected":
        return <CheckCircle2 className="h-4 w-4 text-success" />;
      case "expired":
        return <AlertCircle className="h-4 w-4 text-warning" />;
      default:
        return <XCircle className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const statusLabel = (status: string) => {
    switch (status) {
      case "connected":
        return "Conectado";
      case "expired":
        return "Expirado";
      default:
        return "Desconectado";
    }
  };

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {mockMarketplaces.map((mp, i) => {
        const meta = marketplaceMeta[mp.platform]!;
        return (
          <motion.div
            key={mp.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.1 }}
          >
            <Card className="relative overflow-hidden">
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div
                      className={cn(
                        "flex h-12 w-12 items-center justify-center rounded-xl bg-secondary text-lg font-bold",
                        meta.color
                      )}
                    >
                      {meta.logo}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground capitalize">
                        {mp.platform === "mercadolivre"
                          ? "Mercado Livre"
                          : mp.platform}
                      </p>
                      <div className="mt-0.5 flex items-center gap-1.5">
                        {statusIcon(mp.status)}
                        <span className="text-xs text-muted-foreground">
                          {statusLabel(mp.status)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {mp.status === "connected" && (
                  <div className="mt-4 flex items-center justify-between text-xs text-muted-foreground">
                    <span>{mp.products_synced} produtos sincronizados</span>
                  </div>
                )}

                <div className="mt-4">
                  {mp.status === "connected" ? (
                    <Button variant="outline" size="sm" className="w-full gap-1.5 text-xs">
                      <ExternalLink className="h-3.5 w-3.5" />
                      Gerenciar
                    </Button>
                  ) : (
                    <Button size="sm" className="w-full text-xs">
                      {mp.status === "expired" ? "Reconectar" : "Conectar"}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        );
      })}
    </div>
  );
}

export function ProfilePage() {
  return (
    <div className="mx-auto max-w-3xl">
      <Tabs defaultValue="conta">
        <TabsList className="w-full justify-start">
          <TabsTrigger value="conta" className="gap-1.5">
            <User className="h-4 w-4" />
            Conta
          </TabsTrigger>
          <TabsTrigger value="catalogo" className="gap-1.5">
            <ShoppingBag className="h-4 w-4" />
            Catalogo
          </TabsTrigger>
          <TabsTrigger value="agente" className="gap-1.5">
            <Bot className="h-4 w-4" />
            Agente AI
          </TabsTrigger>
          <TabsTrigger value="marketplaces" className="gap-1.5">
            <Store className="h-4 w-4" />
            Marketplaces
          </TabsTrigger>
        </TabsList>

        <TabsContent value="conta">
          <ContaTab />
        </TabsContent>
        <TabsContent value="catalogo">
          <CatalogoTab />
        </TabsContent>
        <TabsContent value="agente">
          <AgenteTab />
        </TabsContent>
        <TabsContent value="marketplaces">
          <MarketplacesTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
